import { Modal, Button, ModalFuncProps, message } from 'antd';

/**
 * 异步确认对话框
 * 将Modal.confirm转换为Promise形式，便于在async/await中使用
 * 
 * @param options - Modal.confirm的配置选项
 * @returns Promise<boolean> - 用户点击确定返回true，取消返回false
 * 
 * @example
 * // 基本用法
 * const confirmed = await confirmAsync({
 *   title: '确认删除',
 *   content: '确定要删除这条记录吗？',
 *   okText: '删除',
 *   cancelText: '取消'
 * });
 * 
 * if (confirmed) {
 *   // 用户点击了确定
 *   await deleteRecord();
 * }
 * 
 * @example
 * // 在try-catch中使用
 * try {
 *   const confirmed = await confirmAsync({
 *     title: '确认操作',
 *     content: '此操作不可撤销，是否继续？',
 *     okText: '继续',
 *     cancelText: '取消'
 *   });
 *   
 *   if (confirmed) {
 *     await performAction();
 *     message.success('操作成功');
 *   }
 * } catch (error) {
 *   message.error('操作失败');
 * }
 */
export function confirmAsync(options: Omit<ModalFuncProps, 'onOk' | 'onCancel'>): Promise<boolean> {
  return new Promise((resolve) => {
    Modal.confirm({
      ...options,
      onOk: async () => {
        resolve(true);
      },
      onCancel: () => {
        resolve(false);
      },
    });
  });
}

/**
 * 三按钮确认对话框
 * 提供确定、取消、不保存三个选项的确认对话框
 * 
 * @param options - 对话框配置选项
 * @param options.title - 对话框标题
 * @param options.content - 对话框内容
 * @param options.okText - 确定按钮文本，默认为'保存'
 * @param options.cancelText - 取消按钮文本，默认为'取消'
 * @param options.noSaveText - 不保存按钮文本，默认为'不保存'
 * @returns Promise<'ok' | 'cancel' | 'noSave'> - 用户的选择结果
 * 
 * @example
 * // 基本用法
 * const result = await confirmWithThreeButtons({
 *   title: '保存更改',
 *   content: '您有未保存的更改，是否保存？',
 *   okText: '保存',
 *   cancelText: '取消',
 *   noSaveText: '不保存'
 * });
 * 
 * switch (result) {
 *   case 'ok':
 *     await saveChanges();
 *     break;
 *   case 'noSave':
 *     await discardChanges();
 *     break;
 *   case 'cancel':
 *     // 用户取消，不做任何操作
 *     break;
 * }
 */
export function confirmWithThreeButtons(options: {
  title: string;
  content: string;
  okText?: string;
  cancelText?: string;
  noSaveText?: string;
}): Promise<'ok' | 'cancel' | 'noSave'> {
  return new Promise((resolve) => {
    Modal.confirm({
      ...options,
      okText: options.okText || '保存',
      cancelText: options.cancelText || '取消',
      okButtonProps: {
        type: 'primary',
      },
      footer: (_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          <Button onClick={() => {
            Modal.destroyAll();
            resolve('noSave');
          }}>{options.noSaveText || '不保存'}</Button>
          <OkBtn />
        </>
      ),
      onOk: () => {
        resolve('ok');
      },
      onCancel: () => {
        resolve('cancel');
      },
    });
  });
}

export const fallbackCopy = (text: string) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  document.body.appendChild(textarea)
  textarea.select()
  try {
    document.execCommand('copy')
    message.success('复制成功')
  } catch {
    message.error('复制失败')
  }
  document.body.removeChild(textarea)
}
